<template>
  <div id="center-package-items">
    <div class="flex">
      <div
        class="sticky left-80 z-40 h-24 px-6 flex items-center justify-between border-b border-slate-400"
        :style="{ width: 'calc(100vw - 20rem - 0.5rem)' }"
      >
        <div>
          <div class="mb-2 flex items-center gap-2">
            <BlTextTitle4 text="Package Items" />
            <BlBubbleCount :count="totalCreditPackageItems || 0" />
          </div>
          <div>
            <BlTextBody2
              text="Create and manage items to categorize credits for both packages and manual transactions, ensuring consistent pricing and organization."
            />
          </div>
        </div>

        <div class="flex items-center gap-4">
          <BlInputButton icon="plus" label="Item" @clicked="newOpened = true" />
        </div>
      </div>
    </div>

    <NewPackageItemModal v-if="newOpened" @close-modal="newOpened = false" />

    <div class="flex">
      <div
        class="sticky left-80 z-30 py-4 px-6 flex items-center gap-2"
        :style="{ width: 'calc(100vw - 20rem - 0.5rem)' }"
      >
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <BlSort
          class="w-60"
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="defaultSort"
          @change="onChangeSort($event)"
        />
      </div>
    </div>

    <div v-if="pageLoading" class="w-full h-100 grid place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="!creditPackageItems.length"
      class="h-100 grid place-items-center"
    >
      <BlTextBody2
        v-if="searchInput.model"
        text="Oops, the package item(s) not found."
        class="text-slate-700 text-center"
      />
      <BlTextBody2
        v-else
        text="Oops, there are no package item(s) available at this center."
        class="text-slate-700 text-center"
      />
    </div>
    <div v-else class="mb-8">
      <div
        id="package-header"
        class="sticky top-18 z-20 flex items-center flex-grow bg-slate-300 rounded-t"
      >
        <div class="sticky left-80 z-10 pl-6 flex bg-slate-100">
          <BlTooltip
            class="w-60 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400 rounded-tl border-l border-r"
          >
            <BlTextSubtitle3 text="Item" class="truncate" />
          </BlTooltip>
        </div>
        <BlTooltip
          class="w-32 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400"
        >
          <BlTextSubtitle3 text="Status" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="w-48 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400"
        >
          <BlTextSubtitle3 text="Standard retail price" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="w-48 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400"
        >
          <BlTextSubtitle3 text="Provider" class="truncate" />
        </BlTooltip>
        <BlTooltip
          class="w-100 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400 flex-grow"
        >
          <BlTextSubtitle3 text="Description" class="truncate" />
        </BlTooltip>
        <div class="sticky right-0 z-10 pr-6 flex bg-slate-100">
          <div
            class="w-16 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400 rounded-tr border-r"
          >
            <BlTextSubtitle3 text="" />
          </div>
        </div>
      </div>

      <PackageItemItem
        v-for="(creditPackageItem, idx) in creditPackageItems"
        :key="creditPackageItem.id"
        :creditPackageItem="creditPackageItem"
        :isLast="idx + 1 === creditPackageItems.length"
        left="20rem"
        @open-panel="onOpenPanel(creditPackageItem.id)"
      />
    </div>

    <div v-if="totalCreditPackageItems > 25" class="flex mb-36">
      <BlInputPagination
        class="sticky left-86"
        :style="{ width: 'calc(100vw - 20rem - 3rem - 0.5rem)' }"
        :page="page"
        :perPage="perPage"
        :total="totalCreditPackageItems"
        @changed="changePage"
        @changed-per-page="changePerPage"
      />
    </div>
    <div v-else class="mb-46"></div>

    <div
      :data-id="panelId"
      id="panel-details"
      class="fixed top-0 w-116 flex flex-col bg-pure-white border border-slate-400 transition-all duration-300 overflow-y-auto"
      :class="[panelOpened ? 'right-0' : '-right-116']"
      :style="{ height: 'calc(100vh)', zIndex: 101 }"
    >
      <div v-if="panelLoading" class="h-full grid place-items-center">
        <div class="blum-loader"></div>
      </div>
      <PackageItemPanel
        v-else-if="creditPackageItem"
        :data-id="panelId"
        :panelId="panelId"
        :creditPackageItem="creditPackageItem"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import NewPackageItemModal from "./packageItem/NewPackageItemModal.vue";
import PackageItemItem from "./packageItem/Item.vue";
import PackageItemPanel from "./packageItem/PackageItemPanel.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTooltip from "../BlTooltip.vue";
import BlInputButton from "../BlInputButton.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlSort from "../BlSort.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "CenterPackageItems",
  components: {
    NewPackageItemModal,
    PackageItemItem,
    PackageItemPanel,
    //
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlTooltip,
    BlInputButton,
    BlBubbleCount,
    BlInputSearch,
    BlSort,
    BlInputPagination
  },
  data() {
    const defaultSort = { primary: "date", secondary: "asc" };
    return {
      pageLoading: true,
      newOpened: false,
      page: 1,
      perPage: 25,
      searchInput: { placeholder: "Search package item by name", model: "" },
      defaultSort,
      sortInput: defaultSort,
      sortOptions: {
        primary: [
          { value: "date", label: "Date created" },
          { value: "name", label: "Item name" },
          { value: "srp", label: "Standard retail price" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      },
      //
      panelId: "package-item-panel",
      panelOpened: false,
      panelLoading: false
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "creditPackageItem",
      "creditPackageItems",
      "totalCreditPackageItems"
    ]),
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const sort = this.sortInput;

      if (query) params += `&name=${query}`;

      /**
       * - sort mapping -
       * newest -- oldest
       * name_asc -- name_desc
       * srp_asc -- srp_desc
       */
      // prettier-ignore
      const sorts = {
        date: { asc: "newest", desc: "oldest" },
        name: { asc: "name_asc", desc: "name_desc" },
        srp: { asc: "srp_asc", desc: "srp_desc" }
      };
      params += `&sort=${sorts[sort.primary][sort.secondary]}`;
      return params;
    }
  },
  watch: {
    panelOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closePanelHandler);
        }, 10);
      }
    }
  },
  mounted() {
    if (!this.currentCenter.enable_appointment) {
      return this.$router.push({ name: "Center" });
    }
    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["name"]) {
      this.searchInput.model = searchParams["name"];
    }

    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      // prettier-ignore
      const sorts = {
        newest: { primary: "date", secondary: "asc" },
        oldest: { primary: "date", secondary: "desc" },
        name_asc: { primary: "name", secondary: "asc" },
        name_desc: { primary: "name", secondary: "desc" },
        srp_asc: { primary: "srp", secondary: "asc" },
        srp_desc: { primary: "srp", secondary: "desc" }
      };
      if (temp && sorts[temp]) {
        this.sortInput = sorts[temp];
      }
    }

    this.fetchPackageItems();
  },
  methods: {
    ...mapActions(["getCreditPackageItem", "getCreditPackageItems"]),
    async fetchPackageItems() {
      this.pageLoading = true;
      await this.getCreditPackageItems({ params: this.getParams });
      this.pageLoading = false;

      // sync url path for page
      const incomingPath = `/center/package-item${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    //
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchPackageItems();
    }, 1000),
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchPackageItems();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchPackageItems();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchPackageItems();
    },
    //
    onOpenPanel(id) {
      const timeout = this.panelOpened ? 300 : 0;
      this.panelLoading = true;
      setTimeout(async () => {
        this.panelOpened = true;
        await this.getCreditPackageItem({ id });
        this.panelLoading = false;
      }, timeout);
    },
    closePanelHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.panelId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closePanelHandler);
        this.panelOpened = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/CenterPackageItems.vue